<template>
  <template v-if="loading">
    <SfSkeletonLoader
      v-for="i in 15"
      :key="i"
      height="2rem"
      :width="['10rem', '15rem', '20rem'][i % 3]"
      class="tw-px-2"
    />
  </template>
  <template v-else>
    <template v-if="unreadCompanies.length > 0">
      <div
        class="
          tw-uppercase tw-font-medium tw-text-sf-gray-light tw-text-xs tw-py-2
        "
      >
        {{ I18n.t('communication.inbox.unread') }}
      </div>
      <SfList no-margin>
        <SfListItem
          v-for="company in unreadCompanies"
          :key="company.id"
          :item-id="`firm-inbox-v2-unread-${company.id}`"
          background="transparent"
          font-weight="medium"
          cornered
          :selected="activeCompanyId === company.id"
          @item-click="setActiveCompany(company.id)"
        >
          <template #default>{{ company.name }}</template>
          <template #right-content>
            <span
              v-tooltip="I18n.t('communication.inbox.open_client_overview')"
              class="tw-hidden group-hover:tw-inline tw-px-2"
              :class="
                activeCompanyId === company.id
                  ? 'tw-text-sf-snow-lighter'
                  : 'tw-text-sf-primary-dark'
              "
              @click.prevent.stop="openCompany(company.id)"
            >
              <i class="fa fa-external-link" />
            </span>

            <template v-if="anyFilterPresent">
              <span
                v-if="activeCompanyId === company.id"
                class="
                  tw-flex
                  tw-items-center
                  tw-justify-center
                  tw-h-4
                  tw-min-w-4
                  tw-rounded-full
                  tw-px-1
                  tw-leading-3
                  tw-bg-sf-danger
                  tw-text-sf-snow-lighter
                  tw-text-xs
                "
                :data-testid="`firm-inbox-v2-unread-count-${company.id}`"
              >
                {{ filteredUnreadNotificationCount }}
              </span>
            </template>
            <template v-else>
              <span
                class="
                  tw-flex
                  tw-items-center
                  tw-justify-center
                  tw-h-4
                  tw-min-w-4
                  tw-rounded-full
                  tw-px-1
                  tw-leading-3
                  tw-bg-sf-danger
                  tw-text-sf-snow-lighter
                  tw-text-xs
                "
                :data-testid="`firm-inbox-v2-unread-count-${company.id}`"
              >
                {{ company.unreadNotifications }}
              </span>
            </template>
          </template>
        </SfListItem>
      </SfList>
    </template>
    <div
      v-if="unreadCompanies.length > 0 && readCompanies.length > 0"
      class="tw-h-px tw-w-full tw-my-4 tw-bg-sf-neutral-light-1"
    />
    <template v-if="readCompanies.length > 0">
      <div
        data-testid="firm-inbox-v2-read-companies"
        class="
          tw-uppercase tw-font-medium tw-text-sf-gray-light tw-text-xs tw-py-2
        "
      >
        {{ I18n.t('communication.inbox.read') }}
      </div>
      <SfList no-margin>
        <SfListItem
          v-for="company in readCompanies"
          :key="company.id"
          :item-id="`firm-inbox-v2-unread-${company.id}`"
          background="transparent"
          font-weight="medium"
          cornered
          :selected="activeCompanyId === company.id"
          @item-click="setActiveCompany(company.id)"
        >
          <template #default>{{ company.name }}</template>
          <template #right-content>
            <span
              v-tooltip="I18n.t('communication.inbox.open_client_overview')"
              class="tw-hidden group-hover:tw-inline tw-px-2"
              :class="
                activeCompanyId === company.id
                  ? 'tw-text-sf-snow-lighter'
                  : 'tw-text-sf-primary-dark'
              "
              @click.prevent.stop="openCompany(company.id)"
            >
              <i class="fa fa-external-link" />
            </span>
          </template>
        </SfListItem>
      </SfList>
    </template>
  </template>
</template>

<script setup>
import { sortBy } from 'lodash'
import { computed } from 'vue'
import { useStore } from 'vuex'

import SfListItem from '@app/shared/vue/components/sf-list/sf-list-item.vue'
import SfList from '@app/shared/vue/components/sf-list/sf-list.vue'
import SfSkeletonLoader from '@app/shared/vue/components/sf-skeleton-loader.vue'

import { useInboxFiltersStore } from '@app/application/communication/screens/inbox/store/inbox-filters'
import { storeToRefs } from 'pinia'
import useGroupedFilteredCommunications from '@app/application/communication/screens/inbox/composables/use-grouped-filtered-communications'

const { filteredUnreadNotificationCount } = useGroupedFilteredCommunications()

const store = useStore()
const inboxFiltersStore = useInboxFiltersStore()

const { anyFilterPresent } = storeToRefs(inboxFiltersStore)

const activeCompanyId = computed(
  () => store.state.notifications.activeCompanyId
)
const companies = computed(() =>
  sortBy(Object.values(store.state.notifications.companies), 'sortIdx')
)
const unreadCompanies = computed(() => {
  return companies.value.filter((c) => c.unreadNotifications > 0)
})
const readCompanies = computed(() => {
  return companies.value.filter((c) => c.unreadNotifications === 0)
})

const loading = computed(() => store.getters.loadingCompanies)

function openCompany(companyId) {
  window.Turbolinks.visit(
    Silverfin.Routes.company_workflows_path({
      firm_id: Silverfin.currentFirmId,
      company_id: companyId,
    })
  )
}

function setActiveCompany(companyId) {
  store.dispatch('setActiveCompany', companyId)
}
</script>
